import { ChangeEvent, ReactElement, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Dialog } from "@headlessui/react";
import { ReactComponent as CloseIcon } from "components/Icons/close.svg";
import { ReactComponent as SearchIcon } from "components/Icons/search.svg";
import { ReactComponent as TrashIcon } from "components/Icons/trash.svg";
import { AperitifPreset, AperitifPresetBody, aperitifMenuApi } from "lib/api/aperitifMenu";
import { AperitifPresetSidebar } from "./AperitifPresetSidebar";
import { useNavigate } from "react-router";
import { Navigation } from "components/UIComponents/Navigation";
import { SubTitle } from "components/UIComponents/SubTitle";
import { PrimaryButton } from "components/UIComponents/PrimaryButton";
import { SecondaryButton } from "components/UIComponents/SecondaryButton";


export function ManageAperitifsPage(): ReactElement {
    const intl = useIntl();
    const language = intl.locale.toString();
    const navigate = useNavigate();
    const onClose = () => navigate("/");

    const [presets, setPresets] = useState<AperitifPreset[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sidebarPreset, setSidebarPreset] = useState<AperitifPreset | null | "new">(null);

    const loadPresets = useCallback((): Promise<AperitifPreset[]> => {
        setLoading(true);
        return aperitifMenuApi.listAperitifPresets()
            .then(data => {
                const sorted = [...data].sort((a, b) =>
                    (a.body.name?.["de"] ?? "").localeCompare(b.body.name?.["de"] ?? "", "de")
                );
                setPresets(sorted);
                return sorted;
            })
            .catch(() => { setError(intl.formatMessage({ id: "aperitif.list.error" })); return []; })
            .finally(() => setLoading(false));
    }, [intl]);

    useEffect(() => { loadPresets(); }, [loadPresets]);

    const filtered = presets.filter(p => {
        const term = searchTerm.toLowerCase();
        const name = (p.body.name?.[language] ?? p.body.name?.["de"] ?? "").toLowerCase();
        const description = (p.body.description?.[language] ?? p.body.description?.["de"] ?? "").toLowerCase();
        return name.includes(term) || description.includes(term);
    });

    const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);

    const handleSave = async (body: AperitifPresetBody) => {
        setSaveLoading(true);
        setSaveError(null);
        try {
            if (sidebarPreset === "new") {
                const created = await aperitifMenuApi.createAperitifPreset({ body });
                const list = await loadPresets();
                setSidebarPreset(list.find(p => p.id === created.id) ?? created);
            } else if (sidebarPreset) {
                await aperitifMenuApi.updateAperitifPreset({ presetId: sidebarPreset.id, body });
                loadPresets();
            }
        } catch {
            setSaveError(intl.formatMessage({ id: "aperitif.save.error" }));
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (presetId: string) => {
        await aperitifMenuApi.deleteAperitifPreset(presetId);
        loadPresets();
    };

    return (
        <div className="w-full h-screen bg-background flex flex-col">
            <div className="flex-none">
                <Navigation
                    left={<>
                        <SubTitle><FormattedMessage id="aperitif.page.title" /></SubTitle>
                    </>}
                    right={<>
                        <PrimaryButton onClick={()=>setSidebarPreset("new")}>
                            <FormattedMessage id="aperitif.page.newPreset" />
                        </PrimaryButton>
                        <span/>
                        <button onClick={onClose}><CloseIcon/></button>
                    </>}
                />
            </div>

            <div className="flex-1 overflow-hidden">
                <div className="h-full p-6 pt-2 flex flex-col">
                    <div className="relative mb-6">
                        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" />
                        <input
                            type="text"
                            placeholder={intl.formatMessage({ id: "aperitif.search.placeholder" })}
                            className="w-full pl-12 pr-4 py-3 bg-[#EBE8E3] rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-primary"
                            value={searchTerm}
                            onChange={onSearchChange}
                        />
                    </div>

                    {loading && (
                        <div className="flex-1 flex items-center justify-center text-sm">
                            <FormattedMessage id="aperitif.list.loading" />
                        </div>
                    )}
                    {error && (
                        <div className="flex-1 flex items-center justify-center text-sm text-primary">
                            {error}
                        </div>
                    )}
                    {!loading && !error && (
                        <>
                            <div className="mb-4 text-sm">
                                <FormattedMessage id="aperitif.list.count" values={{ count: filtered.length }} />
                            </div>
                            <div className="flex-1 overflow-auto">
                                <div className="space-y-2">
                                    {filtered.map(preset => (
                                        <AperitifPresetListItem
                                            key={preset.id}
                                            preset={preset}
                                            onClick={p => setSidebarPreset(p)}
                                            onDelete={handleDelete}
                                        />
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {sidebarPreset !== null && (
                <AperitifPresetSidebar
                    preset={sidebarPreset === "new" ? undefined : sidebarPreset}
                    onClose={() => setSidebarPreset(null)}
                    onSave={handleSave}
                    loading={saveLoading}
                    error={saveError}
                />
            )}
        </div>
    );
}

interface AperitifPresetListItemProps {
    preset: AperitifPreset;
    onClick: (preset: AperitifPreset) => void;
    onDelete: (presetId: string) => Promise<void>;
}

function AperitifPresetListItem({ preset, onClick, onDelete }: AperitifPresetListItemProps): ReactElement {
    const intl = useIntl();
    const language = intl.locale.toString();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const { body } = preset;
    const name = body.name?.[language] ?? body.name?.["de"] ?? "";
    const description = body.description?.[language] ?? body.description?.["de"];

    const handleConfirmDelete = async () => {
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            await onDelete(preset.id);
            setConfirmOpen(false);
        } catch {
            setDeleteError(intl.formatMessage({ id: "aperitif.delete.error" }));
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <>
            <div
                className="w-full flex items-center gap-4 p-4 bg-white rounded-lg hover:bg-warm-gray transition-colors cursor-pointer text-left"
                onClick={() => onClick(preset)}
            >
                <div className="w-12 h-12 flex-shrink-0 bg-white rounded-lg overflow-hidden">
                    {body.imageUrl && (
                        <img src={body.imageUrl} alt={name} className="w-full h-full object-contain" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-medium mb-1">{name}</div>
                    {description && (
                        <div className="text-sm line-clamp-1">{description}</div>
                    )}
                </div>
                {body.price && (
                    <div className="text-sm flex-shrink-0">
                        {body.price}
                    </div>
                )}
                <button
                    className="flex-shrink-0 p-1 text-primary transition-colors"
                    onClick={e => { e.stopPropagation(); setConfirmOpen(true); }}
                >
                    <TrashIcon className="w-5 h-5" />
                </button>
            </div>

            <ConfirmDeleteModal
                open={confirmOpen}
                onClose={() => { setConfirmOpen(false); setDeleteError(null); }}
                onConfirm={handleConfirmDelete}
                loading={deleteLoading}
                error={deleteError}
            />
        </>
    );
}

interface ConfirmDeleteModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    loading?: boolean;
    error?: string | null;
}

function ConfirmDeleteModal({ open, onClose, onConfirm, loading, error }: ConfirmDeleteModalProps): ReactElement {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            className="fixed z-modal inset-0 overflow-y-auto"
        >
            <div className="flex items-center justify-center min-h-screen">
                <Dialog.Overlay className="fixed inset-0 bg-text opacity-60" />

                <div className="relative flex flex-col w-1/3 max-w-sm mx-auto py-6 px-5 bg-white rounded-lg">
                    <div className="flex-none flex items-center">
                        <Dialog.Title className="flex-auto font-barlow text-lg font-semibold">
                            <FormattedMessage id="aperitif.delete.title" />
                        </Dialog.Title>
                        <button className="flex-none" onClick={onClose}>
                            <CloseIcon className="inline-block w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-auto flex flex-col">
                        <div className="flex-auto pt-5 pb-10">
                            <p><FormattedMessage id="aperitif.delete.text" /></p>
                        </div>

                        {error && (
                            <div className="text-sm text-primary mb-2">{error}</div>
                        )}

                        <div className="flex-none flex gap-2 justify-end items-center">
                            <SecondaryButton onClick={onClose} disabled={loading}>
                                <FormattedMessage id="aperitif.delete.cancel" />
                            </SecondaryButton>
                            <PrimaryButton onClick={onConfirm} disabled={loading}>
                                <FormattedMessage id="aperitif.delete.confirm" />
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}
